import React from 'react';

/**
 * SliderInput - Numeric range slider input
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {number} props.value - Current numeric value
 * @param {Function} props.onChange - Change handler
 * @param {number} props.min - Minimum value (from feature.property.minValue)
 * @param {number} props.max - Maximum value (from feature.property.maxValue)
 * @param {number} props.step - Step increment (from feature.property.step, default: 1)
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const SliderInput = ({ 
    feature, 
    value, 
    onChange,
    min = 0, 
    max = 100, 
    step = 1,
    disabled = false, 
    className = ''
}) => {
    // Ensure value is within bounds
    const clampedValue = Math.min(Math.max(min, value || min), max);
    const displayText = feature?.property?.displayText || feature?.name || 'Value';
    
    const handleSliderChange = (e) => {
        const numericValue = parseFloat(e.target.value);
        onChange({ target: { value: numericValue } });
    };
    
    const handleInputChange = (e) => {
        const numericValue = parseFloat(e.target.value);
        if (!isNaN(numericValue)) {
            onChange({ target: { value: numericValue } });
        }
    };
    
    // Calculate percentage for visual display
    const percentage = ((clampedValue - min) / (max - min)) * 100;
    
    return (
        <div 
            className="slider-input-wrapper"
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
            }}
        >
            {/* Label */}
            <label style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.85rem',
                fontWeight: 600
            }}>
                <span>{displayText}</span>
                <span style={{ 
                    color: 'var(--text-secondary)',
                    fontWeight: 400,
                    fontSize: '0.8rem'
                }}>
                    {clampedValue}
                </span>
            </label>
            
            {/* Slider track */}
            <div style={{
                position: 'relative',
                width: '100%',
                height: '6px',
                backgroundColor: 'var(--border)',
                borderRadius: '3px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1
            }}>
                {/* Active track */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${percentage}%`,
                    background: 'linear-gradient(90deg, var(--accent), var(--accent-hover))',
                    borderRadius: '3px',
                    transition: 'width 0.15s'
                }} />
                
                {/* Slider thumb */}
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={clampedValue}
                    onChange={handleSliderChange}
                    disabled={disabled}
                    style={{
                        position: 'absolute',
                        top: '-4px',
                        left: 0,
                        width: '100%',
                        height: '14px',
                        background: 'transparent',
                        appearance: 'none',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        pointerEvents: disabled ? 'none' : 'auto',
                        outline: 'none'
                    }}
                />
            </div>
            
            {/* Numeric input for precise values */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    minWidth: '40px'
                }}>
                    {min}
                </span>
                <input
                    className={className}
                    type="number"
                    value={clampedValue}
                    onChange={handleInputChange}
                    min={min}
                    max={max}
                    step={step}
                    disabled={disabled}
                    style={{
                        flex: 1,
                        textAlign: 'center',
                    }}
                />
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    minWidth: '40px'
                }}>
                    {max}
                </span>
            </div>
            
            {/* Min/Max labels */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.7rem',
                color: 'var(--text-secondary)'
            }}>
                <span>{min}</span>
                <span>{max}</span>
            </div>
        </div>
    );
};

export default SliderInput;
